import cloneDeep from 'lodash/cloneDeep'
import moment from 'moment-timezone'
import { DateTime } from 'luxon'
import type {
  BpBusinessHourInterval,
  BpProduct,
  BusinessHourInterval,
  BusinessHours,
} from '~/definitions/AllDefinitions'

export function canOrder(product: BpProduct | null, isOrderingEnabled: boolean, timeZone: string) {
  if (!product || product.outOfStock || !isOrderingEnabled) {
    return false
  }
  if (!product.availability || typeof product.availability.default === 'undefined' || product.availability.default) {
    return true
  } else if (
    product.availability &&
    typeof product.availability.default !== 'undefined' &&
    !product.availability.default &&
    !(product.availability.hours &&
      typeof product.availability.hours === 'object' &&
      Object.keys(product.availability.hours).length)
  ) {
    return false
  }
  return isTodayOpen(timeZone, product.availability.hours)
}
export function getHoursFromAndTo(hours?: BusinessHours | null, t?: any, timeZone?: string) {
  if (!hours) {
    return ''
  }
  for (const date in hours) {
    const h = hours[date] as any
    h.day = date
    h.today = isToday(date, timeZone)
    if (h.today) {
      if (h.type === 'closed') {
        return t ? t('CLOSED') : 'CLOSED'
      } else if (h.type === 'twenty_four_hours') {
        return t ? t('TWENTY_FOUR_HOURS') : 'TWENTY_FOUR_HOURS'
      }
      let ret: string
      if (h.formattedHours?.length && h.formattedHours[0]?.from) {
        ret = `${h.formattedHours[0].from} - ${h.formattedHours[0].to}`
        if (h.formattedHours[1]?.from) {
          ret += ` | ${h.formattedHours[1].from} - ${h.formattedHours[1].to}`
        }
      } else if (h.hours?.length && h.hours[0]?.from) {
        ret = `${h.hours[0].from} - ${h.hours[0].to}`
        if (h.hours[1]?.from) {
          ret += ` | ${h.hours[1].from} - ${h.hours[1].to}`
        }
      } else {
        return ''
      }
      return ret
    }
  }
}
export function getOrderedHours(hours?: BusinessHours | null, timeZone?: string) {
  if (!hours) {
    return null
  }
  let res = [] as BpBusinessHourInterval[]
  let todayIdx = -1
  let counter = 0
  for (const date in hours) {
    const h = cloneDeep(hours[date]) as BpBusinessHourInterval
    h.today = isToday(date, timeZone)
    h.day = date
    if (h.today) {
      todayIdx = counter
    }
    res.push(h)
    counter++
  }
  if (todayIdx > 0) {
    const arr = res.slice(0, todayIdx)
    const arr2 = res.slice(todayIdx)
    res = arr2.concat(arr)
  }
  return res
}
export function isToday(date: any, timeZone?: string) {
  const now = timeZone
    ? DateTime.now().setZone(timeZone)
    : DateTime.now()
  const abbreviation = now.toFormat('ccc').toUpperCase()
  return date === abbreviation
}
export function isTodayOpen(timeZone: string, hours?: BusinessHours | null) {
  if (!hours) {
    return false
  }
  const orderedHours = getOrderedHours(hours, timeZone)
  if (!(orderedHours && orderedHours.length)) {
    return false
  }
  const interval = orderedHours[0]
  if (interval.type === 'closed') {
    return false
  } else if (interval.type === 'twenty_four' || interval.type === 'twenty_four_hours') {
    return true
  }
  let isOpen = false
  let to: DateTime
  let from: DateTime
  const now = DateTime.now().setZone(timeZone)
  const date = now.toFormat('yyyy-MM-dd')
  for (const hour of interval.hours) {
    const momentTo = `${date}T${hour.to}`
    const momentFrom = `${date}T${hour.from}`
    to = DateTime.fromISO(momentTo, { zone: timeZone })
    from = DateTime.fromISO(momentFrom, { zone: timeZone })
    if (hour.from > hour.to) {
      to = to.plus({ day: 1 })
    }
    const isBetween = now >= from && now <= to
    if (isBetween) {
      isOpen = true
      break
    }
  }
  return isOpen
}
export function isOvernight(interval: BusinessHourInterval) {
  if (!interval || !(interval.hours && interval.hours.length > 0)) {
    return false
  }
  const sortedHours = [...interval.hours].sort((a, b) => a.from.localeCompare(b.from))
  const toMidnight = sortedHours.findIndex(hour => hour.to === '00:00')
  const fromMidnight = sortedHours.findIndex(hour => hour.from === '00:00')
  return toMidnight >= 0 && fromMidnight >= 0
}
export function roundToNearestFifteenMinutes() {
  return moment().add(15 - (moment().minute() % 15), 'm').valueOf()
}
/**
 *
 * @param source {String|Number|moment.Moment} - String should be moment accepted format. Number should be milliseconds
 * @param after {} - if null will it will be set to current time.
 */
export function isAfter(source: string | moment.Moment | number, after?: string | number | moment.Moment | null) {
  if (source === 'ASAP') {
    return true
  }
  if (!after) {
    after = moment()
  } else if (typeof after === 'string') {
    after = moment(after)
  }
  if (['string', 'number'].includes(typeof source)) {
    source = moment(source)
  }
  return (source as moment.Moment).isAfter(after)
}
